import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";

function numberToWords(amount) {
  const units = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine"
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen"
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety"
  ];

  if (typeof amount === "string") amount = parseFloat(amount);
  if (isNaN(amount)) return "Not a Number";

  const [integerPartRaw, decimalRaw = ""] = amount.toFixed(2).split(".");
  const integerPart = parseInt(integerPartRaw, 10);
  const paisePart = parseInt(decimalRaw, 10);

  const twoDigit = (n) => {
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    const tensWord = tens[Math.floor(n / 10)];
    const unitWord = n % 10 ? ` ${units[n % 10]}` : "";
    return `${tensWord}${unitWord}`.trim();
  };

  const threeDigit = (n) => {
    let str = "";
    const hundreds = Math.floor(n / 100);
    const remainder = n % 100;
    if (hundreds) str += `${units[hundreds]} Hundred`;
    if (remainder) str += `${hundreds ? " and " : ""}${twoDigit(remainder)}`;
    return str.trim();
  };

  const convertIndian = (n) => {
    if (n === 0) return units[0];

    let str = "";
    const crore = Math.floor(n / 10000000);
    if (crore) {
      str += `${convertIndian(crore)} Crore `;
      n %= 10000000;
    }

    const lakh = Math.floor(n / 100000);
    if (lakh) {
      str += `${convertIndian(lakh)} Lakh `;
      n %= 100000;
    }

    const thousand = Math.floor(n / 1000);
    if (thousand) {
      str += `${convertIndian(thousand)} Thousand `;
      n %= 1000;
    }

    if (n > 0) str += threeDigit(n);

    return str.trim();
  };

  const rupeesWords = convertIndian(integerPart);
  const paiseWords = paisePart ? twoDigit(paisePart) : "";

  if (paisePart > 0) {
    return `Rupees ${rupeesWords} and Paise ${paiseWords}`.trim();
  }

  return `Rupees ${rupeesWords}`.trim();
}

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 8,
    fontFamily: "Helvetica"
  },
  header: {
    textAlign: "center"
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 2
  },
  companyAddress: {
    lineHeight: 0.8
  },
  sectionContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    width: "100%"
  },
  column: {
    width: "32%", // slightly less than 33% to account for gaps
    flexShrink: 1,
    lineHeight: 0.8
  },
  sectionTitle: {
    padding: 3,
    textAlign: "center",
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: "5px"
  },
  clientName: {
    fontWeight: "bold",
    marginBottom: 3
  },
  clientName: {
    fontWeight: "bold"
  },
  table: {
    display: "flex",
    width: "100%",
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid"
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 2
  },
  tableHeader: {
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid"
  },
  tableCol: {
    width: "25%",
    padding: 2
  },
  tableColSmall: {
    width: "12.5%"
  },
  invoiceInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15
  },
  totalSection: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  terms: {
    marginTop: 20,
    lineHeight: 1.2,
    fontSize: 9
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30
  },
  amountInWords: {
    marginTop: 10,
    fontStyle: "italic"
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: "#000",
    borderTopStyle: "solid",
    marginVertical: 5
  }
});

const InvoicePDF = ({ data, sizes, loginuser }) => {
  const sub_data = data?.sub_billing_lines;
  // Fixed: Removed Math.min() and ensure minimum height
  const rowHeight = 20; // Approximate height per row in pixels
  const gst_number = loginuser?.u_profile?.cmp_gst?.slice(0, 2);
  const startsWith24 = data?.p_org_gst?.startsWith(gst_number);

  function roundOffAdjustment(amount) {
    const whole = Math.floor(amount);
    const decimal = amount - whole;

    if (decimal >= 0.5) {
      return whole + 1 - amount;
    } else {
      return -decimal;
    }
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={{ ...styles.companyName, marginBottom: "5px" }}>
            {loginuser?.u_profile?.cmp_name}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_addr_line1}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_addr_line2}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_city}-{loginuser?.u_profile?.cmp_pincode}
          </Text>
          <Text style={styles.companyAddress}>
            Mobile: {loginuser?.u_profile?.cmp_ph},
          </Text>
          <Text style={styles.companyAddress}>
            Email: {loginuser?.u_profile?.cmp_email}
          </Text>
          <Text style={{ ...styles.companyAddress, fontWeight: "bold" }}>
            GSTIN: {loginuser?.u_profile?.cmp_gst}
          </Text>
          <Text style={{ ...styles.companyAddress, fontWeight: "bold" }}>
            PAN: {loginuser?.u_profile?.cmp_pan}
          </Text>
        </View>
        <View style={styles.divider} />

        {/* Bill To Section */}
        <View style={styles.sectionContainer}>
          {/* Bill To Column */}
          <View style={styles.column}>
            <View
              style={{ ...styles.sectionTitle, backgroundColor: "#C0C0C0" }}>
              <Text>Bill To</Text>
            </View>
            <Text style={styles.clientName}>{data?.p_org_name}</Text>
            <Text>{data?.p_org_address_line1}</Text>
            <Text>{data?.p_org_address_line2}</Text>
            <Text>{data?.p_org_pincode}</Text>
            <Text style={{ fontWeight: "bold" }}>
              GST # : {data?.p_org_gst}
            </Text>
          </View>

          {/* Ship To Column */}
          <View style={styles.column}>
            <View
              style={{ ...styles.sectionTitle, backgroundColor: "#C0C0C0" }}>
              <Text>Ship To</Text>
            </View>
            <Text style={styles.clientName}>{data?.p_org_name}</Text>
            <Text>{data?.p_org_address_line1}</Text>
            <Text>{data?.p_org_address_line2}</Text>
            <Text>{data?.p_org_pincode}</Text>
            <Text style={{ fontWeight: "bold" }}>
              GST # : {data?.p_org_gst}
            </Text>
          </View>

          {/* Invoice Info Column */}
          <View style={styles.column}>
            <View style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <View
                style={{
                  ...styles.sectionTitle,
                  backgroundColor: "#C0C0C0"
                }}>
                <Text>
                  {data?.bill_status == 2 ? "Tax" : "Proforma"} Invoice
                </Text>
              </View>
              <Text style={{ fontWeight: "bold" }}>
                Invoice # : {data?.bill_invoice}
              </Text>
              <Text style={{ fontWeight: "bold" }}>
                Date :{" "}
                {moment(data?.bill_date, "YYYY-MM-DD HH:mm:ss").format(
                  "DD/MM/YYYY"
                )}
              </Text>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableCol}>Items</Text>
            <Text style={styles.tableCol}>HSN</Text>
            <Text style={styles.tableCol}>Size</Text>
            <Text style={styles.tableCol}>Units</Text>
            <Text style={styles.tableCol}>Weight</Text>
            <Text style={styles.tableCol}>Rate</Text>
            <Text style={styles.tableCol}>Amount</Text>
            <Text style={styles.tableCol}>Total</Text>
          </View>
          {/* Table Rows */}

          {/* Fixed: Ensure the container has enough height for single row */}
          <View style={{ minHeight: rowHeight }}>
            {sub_data?.map((row, index) => {
              const size = sizes?.data?.find(
                ({ size_id }) => size_id === row?.size_id
              );
              const size_data = size
                ? `${Number(size?.size_from)?.toFixed(2)} - ${Number(
                    size?.size_to
                  )?.toFixed(2)}`
                : "Fixed";

              return (
                <View style={{ ...styles.tableRow }} key={index}>
                  <Text
                    style={{ ...styles.tableCol, textTransform: "capitalize" }}>
                    {row?.ser_name + " CERTIFICATE CHARGES"}
                  </Text>
                  <Text style={styles.tableCol}>998346</Text>
                  <Text style={styles.tableCol}>{size_data}</Text>
                  <Text style={styles.tableCol}>{row.bill_pcs}</Text>
                  <Text style={styles.tableCol}>
                    {row.bill_cts?.toFixed(2)}
                  </Text>
                  <Text style={styles.tableCol}>{row?.bill_rate}</Text>
                  <Text style={styles.tableCol}>
                    {row?.bill_amount?.toFixed(2)}
                  </Text>
                  <Text style={styles.tableCol}>
                    {row?.bill_total?.toFixed(2)}
                  </Text>
                </View>
              );
            })}
          </View>
          <View>
            <View
              style={[
                styles.tableRow,
                styles.tableHeader,
                {
                  borderTopWidth: 1,
                  borderTopColor: "#000",
                  borderTopStyle: "solid"
                }
              ]}>
              <Text style={styles.tableColSmall}>GST %</Text>
              <Text style={styles.tableColSmall}>Taxable</Text>
              <Text style={styles.tableColSmall}>CGST</Text>
              <Text style={styles.tableColSmall}>SGST</Text>
              <Text style={styles.tableColSmall}>IGST</Text>
              <Text style={styles.tableColSmall}>Total</Text>
              <Text style={styles.tableColSmall}>Sub Total</Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.bill_total)?.toFixed(2)}
              </Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableColSmall}>
                {" "}
                {Number(loginuser?.u_profile?.cmp_gst_rate)}
              </Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.bill_total)?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.bill_cgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.bill_sgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {!startsWith24 ? data?.bill_igst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {data?.bill_net_amount?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>GST</Text>
              <Text style={styles.tableColSmall}>
                {data?.bill_igst?.toFixed(2)}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableRow,
                borderBottomWidth: 1,
                borderBottomColor: "#000",
                borderBottomStyle: "solid"
              }}>
              <Text style={styles.tableColSmall}>Total</Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.bill_total)?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.bill_cgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.bill_sgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {!startsWith24 ? data?.bill_igst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {data?.bill_net_amount?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>Add/Less</Text>
              <Text style={styles.tableColSmall}>
                {roundOffAdjustment(data?.bill_net_amount).toFixed(2)}
              </Text>
            </View>
            <View style={styles.totalSection}>
              <Text style={styles.sectionTitle}>Net Amount</Text>
              <View
                style={{
                  display: "flex",
                  alignItems: "flex-end"
                }}>
                <Text style={styles.sectionTitle}>
                  {(
                    data?.bill_net_amount +
                    roundOffAdjustment(data?.bill_net_amount)
                  ).toFixed(2)}
                </Text>
                <Text
                  style={{
                    fontWeight: "bold",
                    textTransform: "capitalize",
                    padding: 1
                  }}>
                  {numberToWords(
                    (
                      data?.bill_net_amount +
                      roundOffAdjustment(data?.bill_net_amount)
                    ).toFixed(2)
                  ) + " only"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.sectionContainer}>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8,
                textDecoration: "underline"
              }}>
              Terms & Conditions
            </Text>
            <Text>Reverse charges applicable : No</Text>
          </View>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8,
                textDecoration: "underline"
              }}>
              Bank Details
            </Text>
            <Text>NAME: {loginuser?.u_profile?.cmp_name}</Text>
            <Text>BANK: {loginuser?.u_profile?.bank_name}</Text>
            <Text>AC NO: {loginuser?.u_profile?.bank_acc_no}</Text>
            <Text>BRANCH: {loginuser?.u_profile?.bank_branch}</Text>
            <Text>IFSC: {loginuser?.u_profile?.bank_ifsc_code}</Text>
          </View>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8
              }}>
              For {loginuser?.u_profile?.cmp_name}
            </Text>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                marginTop: "60px"
              }}>
              <Text>Receiver's Signature</Text>
              <Text>Authorised Signature</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
